import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { getAccessToken } from '@lib/msal';
import {
  PoundSterling,
  Wallet,
  ArrowUpRight,
  ArrowDownLeft,
  RefreshCw,
  AlertCircle,
  ExternalLink,
  Building2,
  Calendar,
  TrendingUp,
  Loader2,
  Receipt,
  User,
  HelpCircle,
  CheckCircle2,
  CircleDashed,
  Clock,
  ChevronDown,
  ChevronUp,
  Info,
  PiggyBank,
  CreditCard,
  Landmark,
  Banknote,
  CircleDollarSign,
  BadgePoundSterling,
  LucideIcon,
} from 'lucide-react';
import { PayPalIcon, StripeIcon, NatWestIcon, StarlingIcon, MonzoIcon, RevolutIcon } from '@components/icons/BrandIcons';
import { ComponentType, SVGProps } from 'react';

// Type for any icon component (Lucide or custom SVG)
type IconComponent = LucideIcon | ComponentType<SVGProps<SVGSVGElement>>;

// Icon mapping for custom account icons (includes branded icons)
const iconMap: Record<string, IconComponent> = {
  Wallet,
  PiggyBank,
  Building2,
  CreditCard,
  Landmark,
  Banknote,
  CircleDollarSign,
  BadgePoundSterling,
  Receipt,
  PoundSterling,
  // Branded icons
  PayPal: PayPalIcon,
  Stripe: StripeIcon,
  NatWest: NatWestIcon,
  Starling: StarlingIcon,
  Monzo: MonzoIcon,
  Revolut: RevolutIcon,
};

interface FreeAgentAccountIcon {
  accountId: string;
  accountName: string;
  iconName: string;
}

interface BankAccount {
  id: string;
  name: string;
  type: string;
  currency: string;
  currentBalance: number;
  lastActivityDate: string | null;
  bankName: string | null;
  isPrimary: boolean;
  status: string;
}

interface Transaction {
  id: string;
  date: string;
  description: string;
  fullDescription?: string;
  amount: number;
  accountId: string;
  accountName: string;
  isExplained: boolean;
  unexplainedAmount: number;
  isManual: boolean;
  createdAt: string;
}

interface UserExpense {
  userId: string;
  userName: string;
  email: string;
  totalOutstanding: number;
  expenseCount: number;
}

interface AccountTransactionStatus {
  accountId: string;
  accountName: string;
  unexplainedCount: number;
  unexplainedTotal: number;
}

interface CardAttributionTransaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  unexplainedAmount: number;
  /** 'unexplained' = no explanation; 'unapproved' = explained but marked_for_review */
  status?: 'unexplained' | 'unapproved';
}

interface CardAttribution {
  memberName: string;
  memberEmail: string;
  initials: string;
  debitCardIdentifier: string;
  transactions: CardAttributionTransaction[];
  totalUnexplained: number;
  unapprovedCount?: number;
}

interface FreeAgentOverview {
  company: {
    name: string;
    type: string;
    currency: string;
  } | null;
  bankAccounts: BankAccount[];
  recentTransactions: Transaction[];
  totals: {
    totalBalance: number;
    currency: string;
  };
  userExpenses?: UserExpense[];
  transactionStatus?: AccountTransactionStatus[];
  transactionPeriodDays?: number;
  cardAttributions?: CardAttribution[];
  watchedAccountId?: string | null;
}

// Helper to format period days as human readable
const formatPeriod = (days: number): string => {
  if (days === 7) return '1 week';
  if (days === 14) return '2 weeks';
  if (days === 30) return '1 month';
  if (days === 60) return '2 months';
  if (days === 90) return '3 months';
  return `${days} days`;
};

export default function FreeAgent() {
  const [overview, setOverview] = useState<FreeAgentOverview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [expandedTransactionId, setExpandedTransactionId] = useState<string | null>(null);
  const [accountIcons, setAccountIcons] = useState<FreeAgentAccountIcon[]>([]);
  const [showUnexplainedOnly, setShowUnexplainedOnly] = useState(false);
  const [expandedCardMembers, setExpandedCardMembers] = useState<Set<string>>(new Set());

  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);
  // AbortController for cancelling in-flight requests
  const abortControllerRef = useRef<AbortController | null>(null);
  // Track last refresh time in a ref to avoid useEffect dependency issues
  const lastRefreshRef = useRef<Date | null>(null);

  const fetchOverview = useCallback(async (isAutoRefresh = false) => {
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request with timeout
    abortControllerRef.current = new AbortController();
    const timeoutId = setTimeout(() => {
      abortControllerRef.current?.abort();
    }, 30000); // 30 second timeout

    if (!isAutoRefresh) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const apiBase = (import.meta.env.VITE_API_URL || '').replace(/\/api\/?$/, '');
      const token = await getAccessToken();
      const authHeaders: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};

      // Fetch overview and icon settings in parallel
      const [overviewResponse, iconsResponse] = await Promise.all([
        fetch(`${apiBase}/api/freeagent/overview`, {
          signal: abortControllerRef.current.signal,
          headers: authHeaders,
        }),
        fetch(`${apiBase}/api/settings/freeagent-icons`, {
          signal: abortControllerRef.current.signal,
          headers: authHeaders,
        }),
      ]);

      clearTimeout(timeoutId);

      if (!overviewResponse.ok) {
        throw new Error('Failed to fetch FreeAgent data');
      }

      const data = await overviewResponse.json();

      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setOverview(data.data);
        const now = new Date();
        setLastRefresh(now);
        lastRefreshRef.current = now;

        // Update icon settings if available
        if (iconsResponse.ok) {
          const iconsData = await iconsResponse.json();
          if (iconsData.data?.accountIcons) {
            setAccountIcons(iconsData.data.accountIcons);
          }
        }
      }
    } catch (err) {
      // Ignore abort errors
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to load FreeAgent data');
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    fetchOverview();

    // Auto-refresh when tab becomes visible again (handles browser suspension)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isMountedRef.current) {
        // Check if data is stale (more than 2 minutes old) using ref to avoid dependency issues
        const now = new Date();
        const lastRefreshTime = lastRefreshRef.current;
        const isStale = !lastRefreshTime || (now.getTime() - lastRefreshTime.getTime()) > 2 * 60 * 1000;
        if (isStale) {
          fetchOverview(true); // Silent refresh
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      isMountedRef.current = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchOverview]);

  const formatCurrency = (amount: number, currency: string = 'GBP') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getAccountIcon = (accountId: string, type: string) => {
    // Check for custom icon first
    const customIcon = accountIcons.find(i => i.accountId === accountId);
    if (customIcon) {
      const CustomIcon = iconMap[customIcon.iconName];
      if (CustomIcon) {
        return <CustomIcon className="h-5 w-5" />;
      }
    }

    // Fall back to type-based icons
    switch (type.toLowerCase()) {
      case 'current':
        return <Wallet className="h-5 w-5" />;
      case 'savings':
        return <PiggyBank className="h-5 w-5" />;
      default:
        return <Building2 className="h-5 w-5" />;
    }
  };

  if (isLoading && !overview) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <Loader2 className="h-8 w-8 text-sca-purple-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading FreeAgent data...</p>
        </div>
      </div>
    );
  }

  if (error && !overview) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <AlertCircle className="h-6 w-6 text-red-500" />
            <h3 className="text-lg font-semibold text-red-800">Unable to Load FreeAgent Data</h3>
          </div>
          <p className="text-red-600 mb-4">{error}</p>
          <p className="text-sm text-red-500 mb-4">
            This may be because the FreeAgent API token hasn't been configured, or has expired.
            Please contact an administrator.
          </p>
          <button onClick={() => fetchOverview()} className="btn-primary">
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </button>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-700">
            <strong>Note:</strong> You can still access FreeAgent directly at{' '}
            <a
              href="https://scauk.freeagent.com"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:no-underline inline-flex items-center gap-1"
            >
              scauk.freeagent.com
              <ExternalLink className="h-3 w-3" />
            </a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with refresh */}
      <div className="flex items-center justify-between">
        <div>
          {overview?.company && (
            <p className="text-sm text-gray-500">
              Connected to: <span className="font-medium text-gray-700">{overview.company.name}</span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-4">
          {lastRefresh && (
            <p className="text-xs text-gray-400">
              Updated {lastRefresh.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
          <button
            onClick={() => fetchOverview()}
            disabled={isLoading}
            className="btn-secondary text-sm py-1.5"
          >
            <RefreshCw className={`h-4 w-4 mr-1.5 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <a
            href="https://scauk.freeagent.com"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary text-sm py-1.5"
          >
            Open FreeAgent
            <ExternalLink className="h-3 w-3 ml-1.5" />
          </a>
        </div>
      </div>

      {/* Total Balance Card */}
      {overview && (
        <div className="bg-gradient-to-br from-sca-purple-500 to-sca-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sca-purple-100 text-sm font-medium">Total Balance (Active Accounts)</p>
              <p className="text-3xl font-bold mt-1">
                {formatCurrency(overview.totals.totalBalance, overview.totals.currency)}
              </p>
            </div>
            <div className="h-14 w-14 bg-white/20 rounded-xl flex items-center justify-center">
              <TrendingUp className="h-7 w-7" />
            </div>
          </div>
        </div>
      )}

      {/* Outstanding Expenses & Transaction Status Row */}
      {overview && (
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Outstanding Expenses Card - prominent when has items, subtle when empty */}
          {overview.userExpenses && overview.userExpenses.length > 0 ? (
            <div className="card border-2 border-amber-300 bg-amber-50/50 shadow-lg shadow-amber-100">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-500 rounded-lg">
                    <Receipt className="h-5 w-5 text-white" />
                  </div>
                  <h2 className="text-lg font-semibold text-amber-900">Outstanding Expenses</h2>
                </div>
                <span className="px-2 py-1 bg-amber-500 text-white text-xs font-bold rounded-full">
                  {overview.userExpenses.length} pending
                </span>
              </div>

              <div className="space-y-3">
                {overview.userExpenses.map((expense) => (
                  <div
                    key={expense.userId}
                    className="flex items-center justify-between p-3 bg-white border border-amber-200 rounded-lg shadow-sm"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 bg-amber-500 rounded-full flex items-center justify-center">
                        <User className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{expense.userName}</p>
                        <p className="text-xs text-gray-500">
                          {expense.expenseCount} expense{expense.expenseCount !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-amber-700 text-lg">
                        {formatCurrency(expense.totalOutstanding)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="card bg-gray-50 border-gray-200">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-1.5 bg-gray-200 rounded-lg">
                  <Receipt className="h-4 w-4 text-gray-500" />
                </div>
                <h2 className="text-sm font-medium text-gray-500">Outstanding Expenses</h2>
              </div>
              <p className="text-xs text-gray-400 flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3 text-green-500" />
                None pending
              </p>
            </div>
          )}

          {/* Unexplained Transactions Card */}
          <div className="card">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <HelpCircle className="h-5 w-5 text-blue-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Unexplained Transactions</h2>
            </div>

            {(!overview.transactionStatus || overview.transactionStatus.length === 0) ? (
              <div className="text-center py-6">
                <p className="text-gray-500">No active accounts to check</p>
              </div>
            ) : (
              <div className="space-y-3">
                {overview.transactionStatus?.map((status) => (
                  <div
                    key={status.accountId}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      status.unexplainedCount > 0
                        ? 'bg-red-50 border-red-200'
                        : 'bg-green-50 border-green-200'
                    }`}
                  >
                    <div>
                      <p className="font-medium text-gray-900">{status.accountName}</p>
                      <p className={`text-xs ${
                        status.unexplainedCount > 0 ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {status.unexplainedCount === 0
                          ? 'All transactions explained'
                          : `${status.unexplainedCount} unexplained`}
                      </p>
                    </div>
                    {status.unexplainedCount > 0 && (
                      <div className="text-right">
                        <p className="font-semibold text-red-700">
                          {formatCurrency(status.unexplainedTotal)}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Card Transactions — only shown when watched account is configured and has attributions */}
      {overview && overview.watchedAccountId && overview.cardAttributions && overview.cardAttributions.length > 0 && (
        <div className="card border-2 border-purple-200 bg-purple-50/30">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-sca-purple-500 rounded-lg">
                <CreditCard className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Pending Card Transactions</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Transactions needing attention — unexplained or awaiting approval
                </p>
              </div>
            </div>
            <span className="px-2 py-1 bg-sca-purple-500 text-white text-xs font-bold rounded-full">
              {overview.cardAttributions.length} member{overview.cardAttributions.length !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="space-y-3">
            {overview.cardAttributions.map((attr) => {
              const isExpanded = expandedCardMembers.has(attr.memberEmail);
              const toggle = () => setExpandedCardMembers(prev => {
                const next = new Set(prev);
                if (next.has(attr.memberEmail)) next.delete(attr.memberEmail);
                else next.add(attr.memberEmail);
                return next;
              });

              const unexplainedCount = attr.transactions.filter(tx => tx.status === 'unexplained').length;
              const unapprovedCount  = attr.unapprovedCount ?? attr.transactions.filter(tx => tx.status === 'unapproved').length;
              const totalUnapproved  = attr.transactions
                .filter(tx => tx.status === 'unapproved')
                .reduce((sum, tx) => sum + Math.abs(tx.amount), 0);

              return (
                <div key={attr.memberEmail} className="bg-white rounded-lg border border-purple-200 overflow-hidden">
                  {/* Summary row — always visible */}
                  <button
                    onClick={toggle}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-purple-50/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-sca-purple-100 text-sca-purple-700 flex items-center justify-center text-sm font-bold flex-shrink-0">
                        {attr.initials}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{attr.memberName}</p>
                        <p className="text-xs text-gray-500">
                          {attr.transactions.length} transaction{attr.transactions.length !== 1 ? 's' : ''}
                          {' '}· card ••••{attr.debitCardIdentifier}
                          {unexplainedCount > 0 && unapprovedCount > 0 && (
                            <span className="ml-1">
                              · <span className="text-amber-600">{unexplainedCount} unexplained</span>
                              {', '}
                              <span className="text-orange-500">{unapprovedCount} unapproved</span>
                            </span>
                          )}
                          {unexplainedCount > 0 && unapprovedCount === 0 && (
                            <span className="ml-1 text-amber-600">· {unexplainedCount} unexplained</span>
                          )}
                          {unapprovedCount > 0 && unexplainedCount === 0 && (
                            <span className="ml-1 text-orange-500">· {unapprovedCount} unapproved</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        {unexplainedCount > 0 && (
                          <p className="font-semibold text-amber-600 leading-tight">
                            {formatCurrency(attr.totalUnexplained)}
                            <span className="text-xs font-normal ml-1">unexplained</span>
                          </p>
                        )}
                        {unapprovedCount > 0 && (
                          <p className="font-semibold text-orange-600 leading-tight">
                            {formatCurrency(totalUnapproved)}
                            <span className="text-xs font-normal ml-1">to approve</span>
                          </p>
                        )}
                        {unexplainedCount === 0 && unapprovedCount === 0 && (
                          <p className="font-semibold text-purple-700">{formatCurrency(attr.totalUnexplained)}</p>
                        )}
                      </div>
                      {isExpanded
                        ? <ChevronUp className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        : <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      }
                    </div>
                  </button>

                  {/* Transaction detail table — shown when expanded */}
                  {isExpanded && (
                    <div className="border-t border-purple-100">
                      <table className="min-w-full divide-y divide-gray-100">
                        <thead className="bg-purple-50/50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {attr.transactions.map((tx) => (
                            <tr key={tx.id} className="hover:bg-gray-50">
                              <td className="px-4 py-2 text-sm text-gray-600 whitespace-nowrap">
                                {formatDate(tx.date)}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900 max-w-xs truncate">
                                {tx.description}
                              </td>
                              <td className="px-3 py-2 text-center">
                                {tx.status === 'unapproved' ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                                    <Clock className="h-3 w-3" />
                                    Unapproved
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                    <CircleDashed className="h-3 w-3" />
                                    Unexplained
                                  </span>
                                )}
                              </td>
                              <td className={`px-4 py-2 text-sm text-right whitespace-nowrap font-medium ${
                                tx.amount < 0 ? 'text-red-600' : 'text-green-600'
                              }`}>
                                {formatCurrency(tx.amount)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bank Accounts */}
      {overview && overview.bankAccounts.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Wallet className="h-5 w-5 text-gray-400" />
              <h2 className="text-lg font-semibold text-gray-900">Bank Accounts</h2>
            </div>
            {selectedAccountId && (
              <button
                onClick={() => setSelectedAccountId(null)}
                className="text-sm text-sca-purple-600 hover:text-sca-purple-800 font-medium"
              >
                Show All Transactions
              </button>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {overview.bankAccounts.map((account) => {
              const isSelected = selectedAccountId === account.id;
              const isActive = account.status?.toLowerCase() === 'active';

              return (
                <button
                  type="button"
                  key={account.id}
                  onClick={() => {
                    if (isActive) {
                      setSelectedAccountId(isSelected ? null : account.id);
                    }
                  }}
                  disabled={!isActive}
                  className={`border rounded-lg p-4 text-left transition-all ${
                    isActive
                      ? isSelected
                        ? 'border-sca-purple-500 bg-sca-purple-50 ring-2 ring-sca-purple-200'
                        : 'border-gray-200 bg-white hover:border-sca-purple-300 hover:shadow-md cursor-pointer'
                      : 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={`p-2 rounded-lg ${
                        isSelected
                          ? 'bg-sca-purple-200 text-sca-purple-700'
                          : account.isPrimary
                            ? 'bg-sca-purple-100 text-sca-purple-600'
                            : 'bg-gray-100 text-gray-500'
                      }`}>
                        {getAccountIcon(account.id, account.type)}
                      </div>
                      {account.isPrimary && (
                        <span className="text-xs font-medium text-sca-purple-600 bg-sca-purple-50 px-2 py-0.5 rounded-full">
                          Primary
                        </span>
                      )}
                    </div>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      isActive
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}>
                      {account.status}
                    </span>
                  </div>

                  <h3 className="font-medium text-gray-900 mb-1">{account.name}</h3>
                  {account.bankName && (
                    <p className="text-xs text-gray-500 mb-2">{account.bankName}</p>
                  )}

                  <p className={`text-xl font-semibold ${
                    account.currentBalance >= 0 ? 'text-gray-900' : 'text-red-600'
                  }`}>
                    {formatCurrency(account.currentBalance, account.currency)}
                  </p>

                  {account.lastActivityDate && (
                    <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Last activity: {formatDate(account.lastActivityDate)}
                    </p>
                  )}

                  {isSelected && (
                    <p className="text-xs text-sca-purple-600 mt-2 font-medium">
                      ✓ Showing transactions
                    </p>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Transactions */}
      {overview && overview.recentTransactions.length > 0 && (() => {
        // Apply account filter
        let filteredTransactions = selectedAccountId
          ? overview.recentTransactions.filter(t => t.accountId === selectedAccountId)
          : overview.recentTransactions;

        // Apply unexplained filter
        if (showUnexplainedOnly) {
          filteredTransactions = filteredTransactions.filter(t => !t.isExplained);
        }

        const selectedAccount = selectedAccountId
          ? overview.bankAccounts.find(a => a.id === selectedAccountId)
          : null;

        const unexplainedCount = overview.recentTransactions.filter(t => !t.isExplained).length;

        return (
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <PoundSterling className="h-5 w-5 text-gray-400" />
                <h2 className="text-lg font-semibold text-gray-900">Recent Transactions</h2>
                {selectedAccount ? (
                  <span className="text-sm text-sca-purple-600 font-medium">
                    — {selectedAccount.name}
                  </span>
                ) : (
                  <span className="text-sm text-gray-400">
                    (Last {formatPeriod(overview.transactionPeriodDays || 30)}, all accounts)
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                {/* Unexplained filter toggle */}
                {unexplainedCount > 0 && (
                  <button
                    onClick={() => setShowUnexplainedOnly(!showUnexplainedOnly)}
                    className={`text-sm px-3 py-1.5 rounded-lg font-medium transition-colors flex items-center gap-1.5 ${
                      showUnexplainedOnly
                        ? 'bg-amber-100 text-amber-700 border border-amber-300'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
                    }`}
                  >
                    <CircleDashed className="h-4 w-4" />
                    Unexplained ({unexplainedCount})
                  </button>
                )}
                {/* Quick link to FreeAgent */}
                <a
                  href="https://scauk.freeagent.com/banking"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-sca-purple-600 hover:text-sca-purple-800 font-medium flex items-center gap-1"
                >
                  Open in FreeAgent
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>

            {filteredTransactions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No transactions found for this account</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8">
                        <span className="sr-only">Expand</span>
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      {!selectedAccountId && (
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Account
                        </th>
                      )}
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-10" title="Status">
                        <Info className="h-3 w-3 mx-auto" />
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredTransactions.map((transaction) => {
                      const isExpanded = expandedTransactionId === transaction.id;

                      return (
                        <Fragment key={transaction.id}>
                          <tr
                            onClick={() => setExpandedTransactionId(isExpanded ? null : transaction.id)}
                            className="hover:bg-gray-50 cursor-pointer"
                          >
                            <td className="px-2 py-3 text-gray-400">
                              {isExpanded ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(transaction.date)}
                            </td>
                            {!selectedAccountId && (
                              <td className="px-3 py-3 text-sm text-gray-600">
                                <span className="truncate block max-w-[100px]" title={transaction.accountName}>
                                  {transaction.accountName}
                                </span>
                              </td>
                            )}
                            <td className="px-3 py-3 text-sm text-gray-900">
                              <div className="flex items-center gap-2">
                                {transaction.amount >= 0 ? (
                                  <ArrowDownLeft className="h-4 w-4 text-green-500 flex-shrink-0" />
                                ) : (
                                  <ArrowUpRight className="h-4 w-4 text-red-500 flex-shrink-0" />
                                )}
                                <span className="truncate max-w-xs">{transaction.description}</span>
                              </div>
                            </td>
                            <td className="px-2 py-3 text-center">
                              <div className="relative group">
                                {transaction.isExplained ? (
                                  <CheckCircle2 className="h-4 w-4 text-green-500 mx-auto" />
                                ) : (
                                  <CircleDashed className="h-4 w-4 text-amber-500 mx-auto" />
                                )}
                                {/* Tooltip */}
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                                  {transaction.isExplained ? 'Explained' : 'Unexplained'}
                                </div>
                              </div>
                            </td>
                            <td className={`px-3 py-3 whitespace-nowrap text-sm text-right font-medium ${
                              transaction.amount >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {transaction.amount >= 0 ? '+' : ''}
                              {formatCurrency(transaction.amount)}
                            </td>
                          </tr>
                          {/* Expanded details row */}
                          {isExpanded && (
                            <tr className="bg-gray-50">
                              <td colSpan={selectedAccountId ? 5 : 6} className="px-4 py-3">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                  <div>
                                    <p className="text-xs text-gray-500 uppercase">Account</p>
                                    <p className="font-medium text-gray-900">{transaction.accountName}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500 uppercase">Status</p>
                                    <p className={`font-medium ${transaction.isExplained ? 'text-green-600' : 'text-amber-600'}`}>
                                      {transaction.isExplained ? 'Explained' : 'Unexplained'}
                                    </p>
                                  </div>
                                  {!transaction.isExplained && transaction.unexplainedAmount !== 0 && (
                                    <div>
                                      <p className="text-xs text-gray-500 uppercase">Unexplained Amount</p>
                                      <p className="font-medium text-amber-600">
                                        {formatCurrency(Math.abs(transaction.unexplainedAmount))}
                                      </p>
                                    </div>
                                  )}
                                  <div>
                                    <p className="text-xs text-gray-500 uppercase">Entry Type</p>
                                    <p className="font-medium text-gray-900">
                                      {transaction.isManual ? 'Manual' : 'Bank Feed'}
                                    </p>
                                  </div>
                                  {transaction.fullDescription && transaction.fullDescription !== transaction.description && (
                                    <div className="col-span-2 md:col-span-4">
                                      <p className="text-xs text-gray-500 uppercase">Full Description</p>
                                      <p className="font-medium text-gray-900">{transaction.fullDescription}</p>
                                    </div>
                                  )}
                                  <div>
                                    <p className="text-xs text-gray-500 uppercase">Created</p>
                                    <p className="font-medium text-gray-900">{formatDate(transaction.createdAt)}</p>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })()}

      {/* Info note */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-700">
          <strong>Note:</strong> This is a read-only view of your FreeAgent data.
          To make changes or view more details, please use{' '}
          <a
            href="https://scauk.freeagent.com"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:no-underline inline-flex items-center gap-1"
          >
            FreeAgent directly
            <ExternalLink className="h-3 w-3" />
          </a>
          .
        </p>
      </div>
    </div>
  );
}
